import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  ArrowUpCircle,
  Search,
  Eye,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  Loader2,
  RefreshCw,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import Pagination from "@/components/Pagination";

export default function Topups() {
  const [topups, setTopups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedTopup, setSelectedTopup] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    pending: 0,
    failed: 0,
    totalAmount: 0,
  });

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Sorting states
  const [sortColumn, setSortColumn] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");

  useEffect(() => {
    loadTopups();
    loadStats();
  }, [statusFilter]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter]);

  const loadTopups = async () => {
    try {
      setLoading(true);

      let query = supabase
        .from("topups")
        .select("*")
        .order("created_at", { ascending: false });

      if (statusFilter !== "all") {
        query = query.eq("status", statusFilter);
      }

      const { data: topupsData, error: topupsError } = await query;

      if (topupsError) throw topupsError;

      // Fetch card info for each topup using card_number
      const topupsWithCard = await Promise.all(
        (topupsData || []).map(async (topup) => {
          if (topup.card_number && !topup.card_number.includes("XXXXXX")) {
            try {
              const { data: cardData, error: cardError } = await supabase
                .from("cards")
                .select(
                  "card_number, card_status, email, first_name, last_name"
                )
                .eq("card_number", topup.card_number)
                .maybeSingle();

              if (!cardError && cardData) {
                return { ...topup, card: cardData };
              }
            } catch (err) {
              // Silently ignore card lookup errors (RLS or other issues)
              console.log(
                "Card lookup failed for topup:",
                topup.id,
                err.message
              );
            }
          }
          return { ...topup, card: null };
        })
      );

      setTopups(topupsWithCard);
    } catch (error) {
      console.error("Error loading topups:", error);
      toast.error(`Failed to load topups: ${error.message || error}`);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const { data, error } = await supabase
        .from("topups")
        .select("status, amount");

      if (error) throw error;

      const completedTopups = data.filter((t) => t.status === "completed");
      const totalAmount = completedTopups.reduce(
        (sum, t) => sum + (t.amount || 0),
        0
      );

      const stats = {
        total: data.length,
        completed: completedTopups.length,
        pending: data.filter((t) => t.status === "pending").length,
        failed: data.filter((t) => t.status === "failed").length,
        totalAmount: totalAmount,
      };

      setStats(stats);
    } catch (error) {
      console.error("Error loading stats:", error);
    }
  };

  const handleRefund = async (topupId) => {
    if (!confirm("Are you sure you want to refund this top-up?")) return;

    try {
      const { error } = await supabase
        .from("topups")
        .update({
          status: "refunded",
          processed_at: new Date().toISOString(),
        })
        .eq("id", topupId);

      if (error) throw error;

      toast.success("Top-up has been refunded");
      loadTopups();
      loadStats();
    } catch (error) {
      console.error("Error refunding topup:", error);
      toast.error("Failed to refund top-up");
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { variant: "secondary", icon: Clock, label: "Pending" },
      processing: { variant: "default", icon: RefreshCw, label: "Processing" },
      completed: { variant: "success", icon: CheckCircle, label: "Completed" },
      failed: { variant: "destructive", icon: XCircle, label: "Failed" },
      refunded: { variant: "warning", icon: AlertCircle, label: "Refunded" },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const formatCardNumber = (number) => {
    if (!number) return "";
    return `****-****-****-${number.slice(-4)}`;
  };

  const formatAmount = (amount) => {
    return (amount || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const filteredTopups = topups.filter((topup) => {
    const searchLower = searchQuery.toLowerCase();
    const cardEmail = topup.card?.email || topup.email || "";
    const cardName = `${topup.card?.first_name || ""} ${
      topup.card?.last_name || ""
    }`;

    return (
      cardEmail.toLowerCase().includes(searchLower) ||
      cardName.toLowerCase().includes(searchLower) ||
      topup.card_number?.toLowerCase().includes(searchLower) ||
      topup.transaction_hash?.toLowerCase().includes(searchLower)
    );
  });

  // Sort function
  const sortedTopups = [...filteredTopups].sort((a, b) => {
    let aValue, bValue;

    switch (sortColumn) {
      case "transaction":
        aValue = a.transaction_hash || a.id;
        bValue = b.transaction_hash || b.id;
        break;
      case "card":
        aValue = a.card_number || "";
        bValue = b.card_number || "";
        break;
      case "user":
        aValue = a.card?.email || a.email || "";
        bValue = b.card?.email || b.email || "";
        break;
      case "amount":
        aValue = a.amount || 0;
        bValue = b.amount || 0;
        break;
      case "status":
        aValue = a.status || "";
        bValue = b.status || "";
        break;
      case "date":
        aValue = new Date(a.created_at).getTime();
        bValue = new Date(b.created_at).getTime();
        break;
      default:
        aValue = new Date(a.created_at).getTime();
        bValue = new Date(b.created_at).getTime();
    }

    if (typeof aValue === "string" && typeof bValue === "string") {
      return sortDirection === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
  });

  // Pagination calculations
  const totalPages = Math.ceil(sortedTopups.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedTopups = sortedTopups.slice(startIndex, endIndex);

  // Handle column sort
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  // Sort icon component
  const SortIcon = ({ column }) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    return sortDirection === "asc" ? (
      <ArrowUp className="h-4 w-4 ml-1" />
    ) : (
      <ArrowDown className="h-4 w-4 ml-1" />
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h2 className="text-2xl font-bold">Top-up Transactions</h2>
        <p className="text-muted-foreground">
          Monitor and manage card top-up transactions
        </p>
      </div>

      {/* Statistics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total</CardTitle>
            <ArrowUpCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completed</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.completed}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <Clock className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.pending}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Failed</CardTitle>
            <XCircle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.failed}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Amount</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${formatAmount(stats.totalAmount)}
            </div>
            <p className="text-xs text-muted-foreground">USDT</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <CardTitle>Top-up History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by email, card, or transaction..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="refunded">Refunded</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Topups Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <button
                      onClick={() => handleSort("transaction")}
                      className="flex items-center hover:text-foreground transition-colors cursor-pointer"
                    >
                      Transaction
                      <SortIcon column="transaction" />
                    </button>
                  </TableHead>
                  <TableHead>
                    <button
                      onClick={() => handleSort("card")}
                      className="flex items-center hover:text-foreground transition-colors cursor-pointer"
                    >
                      Card
                      <SortIcon column="card" />
                    </button>
                  </TableHead>
                  <TableHead>
                    <button
                      onClick={() => handleSort("user")}
                      className="flex items-center hover:text-foreground transition-colors cursor-pointer"
                    >
                      User
                      <SortIcon column="user" />
                    </button>
                  </TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>
                    <button
                      onClick={() => handleSort("amount")}
                      className="flex items-center hover:text-foreground transition-colors cursor-pointer"
                    >
                      Amount
                      <SortIcon column="amount" />
                    </button>
                  </TableHead>
                  <TableHead>
                    <button
                      onClick={() => handleSort("status")}
                      className="flex items-center hover:text-foreground transition-colors cursor-pointer"
                    >
                      Status
                      <SortIcon column="status" />
                    </button>
                  </TableHead>
                  <TableHead>
                    <button
                      onClick={() => handleSort("date")}
                      className="flex items-center hover:text-foreground transition-colors cursor-pointer"
                    >
                      Date
                      <SortIcon column="date" />
                    </button>
                  </TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedTopups.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <div className="text-muted-foreground">
                        <ArrowUpCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p className="text-lg font-medium">No top-ups found</p>
                        <p className="text-sm">
                          Top-up transactions will appear here.
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedTopups.map((topup) => (
                    <TableRow key={topup.id}>
                      <TableCell className="font-mono text-xs">
                        {topup.transaction_hash
                          ? `${topup.transaction_hash.slice(0, 8)}...`
                          : topup.id.slice(0, 8)}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {formatCardNumber(topup.card_number)}
                      </TableCell>
                      <TableCell className="text-sm">
                        {topup.card?.email || topup.email || "-"}
                      </TableCell>
                      <TableCell className="text-xs">
                        {topup.source ? (
                          <div className="flex flex-col">
                            <span
                              className={
                                topup.source === "lumy"
                                  ? "font-semibold text-cyan-600"
                                  : "font-medium text-gray-600"
                              }
                            >
                              {topup.source}
                            </span>
                            {topup.source_user && (
                              <span className="text-muted-foreground">
                                {topup.source_user}
                              </span>
                            )}
                          </div>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell className="font-medium">
                        ${formatAmount(topup.amount)}
                      </TableCell>
                      <TableCell>{getStatusBadge(topup.status)}</TableCell>
                      <TableCell className="text-xs">
                        {new Date(topup.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedTopup(topup);
                              setShowDetailsModal(true);
                            }}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {topup.status === "completed" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRefund(topup.id)}
                            >
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {sortedTopups.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={sortedTopups.length}
              onPageChange={setCurrentPage}
              onPageSizeChange={(newSize) => {
                setPageSize(newSize);
                setCurrentPage(1);
              }}
            />
          )}
        </CardContent>
      </Card>

      {/* Topup Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Top-up Details</DialogTitle>
            <DialogDescription>
              Transaction: {selectedTopup?.id.slice(0, 8)}...
            </DialogDescription>
          </DialogHeader>

          {selectedTopup && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Status</Label>
                  <p className="text-sm">
                    {getStatusBadge(selectedTopup.status)}
                  </p>
                </div>
                <div>
                  <Label>Currency</Label>
                  <p className="text-sm">{selectedTopup.currency}</p>
                </div>
                <div>
                  <Label>Amount</Label>
                  <p className="text-sm font-medium">
                    ${formatAmount(selectedTopup.amount)}
                  </p>
                </div>
                <div>
                  <Label>Card</Label>
                  <p className="text-sm font-mono">
                    {formatCardNumber(selectedTopup.card_number)}
                  </p>
                </div>
                <div className="col-span-2">
                  <Label>Card Holder</Label>
                  <p className="text-sm">
                    {selectedTopup.card?.email || selectedTopup.email || "-"}
                  </p>
                  {selectedTopup.card && (
                    <p className="text-xs text-muted-foreground">
                      {selectedTopup.card?.first_name}{" "}
                      {selectedTopup.card?.last_name}
                    </p>
                  )}
                </div>
                {selectedTopup.transaction_hash && (
                  <div className="col-span-2">
                    <Label>Transaction Hash</Label>
                    <p className="text-xs font-mono break-all">
                      {selectedTopup.transaction_hash}
                    </p>
                  </div>
                )}
                <div>
                  <Label>Created</Label>
                  <p className="text-sm">
                    {new Date(selectedTopup.created_at).toLocaleString()}
                  </p>
                </div>
                {selectedTopup.processed_at && (
                  <div>
                    <Label>Processed</Label>
                    <p className="text-sm">
                      {new Date(selectedTopup.processed_at).toLocaleString()}
                    </p>
                  </div>
                )}
                {selectedTopup.failed_reason && (
                  <div className="col-span-2">
                    <Label>Failure Reason</Label>
                    <p className="text-sm text-destructive">
                      {selectedTopup.failed_reason}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
